import React, {
  useEffect, useLayoutEffect, useState, useRef,
} from 'react';
import { useSpring, animated } from 'react-spring';
import styled from '@emotion/styled';
import Checkmark from '../../../../../shared/_TBComponents/Checkmark';
import Spinner from '../../../../../shared/_TBComponents/Spinner';
import getAllUrlParams from '../../../../../shared/utils/getAllUrlParams';
import useLogin from '../../hooks/useLogin';

// Token parameters that should not be preserved in redirect URL
const TOKEN_PARAMS = ['accessToken', 'refreshToken', 'v2Token', 'sessionId', 'sessionKey', 'tokenExpiration'];

// Pages that only exist in PHP app
const PHP_ONLY_PAGES = [
  'feed',
  'calendar',
  'whiteboard',
  'maxes',
  'journal',
  'manage_users',
  'exercises',
  'goals',
  'printable',
  'attendance',
  'integrations',
  'billing',
];

const buildRedirectUrl = (currentUser, params) => {
  const isAthlete = currentUser.admin === 0;
  const defaultPath = isAthlete ? 'workouts' : 'dashboard';
  const targetPath = params.rel === undefined ? defaultPath : params.rel;

  // Default to webapp; use PHP only if page is in PHP_ONLY_PAGES
  const shouldUsePhp = PHP_ONLY_PAGES.includes(params.rel);

  let domain;
  if (process.env.PHP_APP_URL && process.env.WEBAPP_URL) {
    domain = shouldUsePhp ? process.env.PHP_APP_URL : process.env.WEBAPP_URL;
  } else {
    const isDevelop = process.env.ENVIRONMENT === 'DEVELOP' ? 'dev.' : '';
    const subdomain = shouldUsePhp ? 'app' : 'app-v3';
    domain = `https://${process.env.ENVIRONMENT === 'STAGING' ? 'staging.' : isDevelop}${subdomain}.teambuildr.com`;
  }

  // Preserve query parameters (excluding rel and version)
  const preservedParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (key !== 'rel' && key !== 'version') {
      preservedParams.set(key, params[key]);
    }
  });
  const queryString = preservedParams.toString();

  return `${domain}/${targetPath}${queryString ? `?${queryString}` : ''}`;
};

export const SpinnerContainer = styled('div')`
  display: flex;
  height: 100%;
  flex: 1;
  position: absolute;
  top: 0;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-left: -54px;
  z-index: 0;

  .checkmark {
    display: flex;
    align-self: center;
  }
`;

const ErrorMessage = styled('div')`
  padding: 20px;
  font-size: 14px;
  color: #ff6600;
  text-align: center;
  max-width: 400px;
  margin: 0 auto;
`;

const CenteredErrorContainer = styled('div')`
  display: flex;
  height: 100%;
  flex: 1;
  position: absolute;
  top: 0;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-left: -54px;
`;

const renderError = (message) => (
  <CenteredErrorContainer>
    <ErrorMessage>{message}</ErrorMessage>
  </CenteredErrorContainer>
);

const LoginFormSpear = () => {
  const [hasTokens, setHasTokens] = useState(true);
  const [showMissingTokensError, setShowMissingTokensError] = useState(false);
  const [timeoutError, setTimeoutError] = useState(false);
  const [hasProcessedParams, setHasProcessedParams] = useState(false);

  const preservedParamsRef = useRef({});
  const isSpearLoginLoadingRef = useRef(false);

  const {
    handleSpearLogin,
    isSpearLoginLoading,
    isAuthenticated,
    spearLoginError,
    currentUser,
  } = useLogin();

  // Keep ref in sync with Redux state
  useEffect(() => {
    isSpearLoginLoadingRef.current = isSpearLoginLoading;
  }, [isSpearLoginLoading]);

  useEffect(() => {
    // Only process URL params once to prevent race condition when URL is cleared
    if (hasProcessedParams) {
      return undefined;
    }

    const urlParams = getAllUrlParams(window.location.href, false);

    // Preserve non-token query parameters for redirect (e.g., hideNav, rel, version)
    const preserved = {};
    Object.keys(urlParams).forEach((key) => {
      if (!TOKEN_PARAMS.includes(key)) {
        preserved[key] = urlParams[key];
      }
    });
    preservedParamsRef.current = preserved;

    setHasProcessedParams(true);

    // Check if we have the required token parameters
    if (urlParams.accessToken && urlParams.refreshToken) {
      setHasTokens(true);
      const tokens = {
        accessToken: urlParams.accessToken,
        refreshToken: urlParams.refreshToken,
        v2Token: urlParams.v2Token,
        sessionId: urlParams.sessionId,
        sessionKey: urlParams.sessionKey,
        tokenExpiration: urlParams.tokenExpiration,
      };

      handleSpearLogin(tokens);

      // Clear URL parameters to hide tokens from browser history
      window.history.replaceState({}, document.title, window.location.pathname);

      // Set timeout to detect stuck loading state (30 seconds)
      const timeout = setTimeout(() => {
        if (isSpearLoginLoadingRef.current) {
          setTimeoutError(true);
        }
      }, 30000);

      return () => clearTimeout(timeout);
    }

    const errorTimeout = setTimeout(() => {
      setShowMissingTokensError(true);
    }, 1000);

    return () => clearTimeout(errorTimeout);
  }, [hasProcessedParams]);

  // Handle redirect after successful authentication
  useLayoutEffect(() => {
    if (isAuthenticated && currentUser && Object.keys(currentUser).length > 0) {
      const redirectUrl = buildRedirectUrl(currentUser, preservedParamsRef.current);
      window.location.href = redirectUrl;
    }
  }, [isAuthenticated, currentUser]);

  const showSpinner = !hasTokens && !showMissingTokensError;
  const spinnerOpacity = (isSpearLoginLoading || showSpinner) ? 1 : 0;
  const spinnerAnimation = useSpring({ opacity: spinnerOpacity, zIndex: 0 });
  const completeAnimation = useSpring({ opacity: isAuthenticated ? 1 : 0 });

  // Show error if no tokens provided (after delay)
  if (!hasTokens && showMissingTokensError) {
    return renderError(
      'Authentication tokens are missing. Please ensure you are accessing this page from the Spear integration.',
    );
  }

  // Show timeout error
  if (timeoutError) {
    return renderError(
      'Authentication is taking longer than expected. Please try again or contact support if the issue persists.',
    );
  }

  return (
    <>
      {spearLoginError && renderError(
        'Unable to authenticate via Spear. Please try again or contact support if the issue persists.',
      )}
      <animated.div style={spinnerAnimation}>
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      </animated.div>
      <animated.div style={completeAnimation}>
        <SpinnerContainer>
          <Checkmark />
        </SpinnerContainer>
      </animated.div>
    </>
  );
};

export default LoginFormSpear;
